/**
 * `placementScale.ts` — port of Campfire's import placement-scale heuristic.
 *
 * Mirrors `PlacementModelBase.BestScale(bounds)` → `ScaleUtil.ToClosestUnitScale`
 * (`Models/SceneModel/PlacementModelBase.cs:119`, `Models/Utils/ScaleUtil.cs:119`).
 * Campfire guesses the model's authoring units from its largest bounding-box
 * dimension and returns the metres-per-unit factor to bring it to a sensible
 * real-world size in the scene:
 *
 *   maxDim ≤ 1   → Meters       → 1
 *   maxDim ≤ 3   → Feet         → 0.3048
 *   maxDim ≤ 40  → Inches       → 0.0254
 *   maxDim ≤ 100 → Centimeters  → 0.01
 *   else         → Millimeters  → 0.001
 *
 * (Unity's other BestScale inputs — Campfire-authored `CampfireUnits` metadata
 * and Pixyz-converted flags — don't apply to a plain user-picked GLB, so we
 * always take the bounds-derived branch, matching `AssetFactory.cs:326-333`.)
 */

import { Box3, type Object3D, Vector3 } from "three";

export interface ModelBounds {
  /** World-space size of the model's bounding box (pre-scale). */
  readonly size: Vector3;
  /** World-space center of the model's bounding box (pre-scale). */
  readonly center: Vector3;
  /** Largest of the three size components. */
  readonly maxDimension: number;
}

const K_METERS = 1;
const K_FEET = 0.3048;
const K_INCHES = 0.0254;
const K_CENTIMETERS = 0.01;
const K_MILLIMETERS = 0.001;

/** Compute the model's bounding box. Returns a zero box for empty geometry. */
export function computeModelBounds(root: Object3D): ModelBounds {
  const box = new Box3().setFromObject(root);
  if (box.isEmpty()) {
    return { size: new Vector3(), center: new Vector3(), maxDimension: 0 };
  }
  const size = box.getSize(new Vector3());
  const center = box.getCenter(new Vector3());
  return { size, center, maxDimension: Math.max(size.x, size.y, size.z) };
}

/** Port of `ScaleUtil.ToClosestUnitScale`. `maxDimension` is in native units. */
export function bestPlacementScale(maxDimension: number): number {
  if (!Number.isFinite(maxDimension) || maxDimension <= 0) return K_METERS;
  if (maxDimension <= 1) return K_METERS;
  if (maxDimension <= 3) return K_FEET;
  if (maxDimension <= 40) return K_INCHES;
  if (maxDimension <= 100) return K_CENTIMETERS;
  return K_MILLIMETERS;
}
